import type { FocusAction, BlurAction, ActionResult } from '../types/actions';
import { isElementVisible, scrollIntoView } from '../utils/dom-utils';
import { waitForElement } from '../utils/wait-utils';

export async function executeFocusAction(action: FocusAction): Promise<ActionResult> {
  const startTime = Date.now();

  try {
    // Find the element
    let element = document.querySelector<HTMLElement>(action.selector);

    // Wait for element if specified
    if (!element && action.waitForElement) {
      element = await waitForElement<HTMLElement>(
        action.selector,
        action.waitForElement
      );
    }

    if (!element) {
      return {
        success: false,
        action,
        error: `Element not found: ${action.selector}`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: false
      };
    }

    // Check if visible
    if (!isElementVisible(element)) {
      return {
        success: false,
        action,
        error: 'Element is not visible',
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Scroll into view if needed
    if (action.scrollIntoView !== false) {
      scrollIntoView(element);
    }

    // Save currently focused element so we can restore it after highlighting
    const previousActiveElement = document.activeElement as HTMLElement | null;

    // Focus the element
    element.focus({ preventScroll: true });

    // Dispatch focus event
    const focusEvent = new FocusEvent('focus', {
      bubbles: true,
      cancelable: true,
      view: window
    });
    element.dispatchEvent(focusEvent);

    // Dispatch focusin event (bubbling version)
    const focusinEvent = new FocusEvent('focusin', {
      bubbles: true,
      cancelable: true,
      view: window
    });
    element.dispatchEvent(focusinEvent);

    // Restore focus to EVA widget input (prevents stealing focus during highlighting)
    element.blur();
    const evaInput = document.querySelector('.eva-input') as HTMLElement | null;
    if (evaInput) {
      try { evaInput.focus({ preventScroll: true }); } catch (_e) { /* ignore */ }
    } else if (previousActiveElement && previousActiveElement !== document.body && previousActiveElement !== element) {
      try { previousActiveElement.focus({ preventScroll: true }); } catch (_e) { /* ignore */ }
    }

    return {
      success: true,
      action,
      timestamp: Date.now(),
      duration: Date.now() - startTime,
      elementFound: true
    };
  } catch (error) {
    return {
      success: false,
      action,
      error: error instanceof Error ? error.message : String(error),
      timestamp: Date.now(),
      duration: Date.now() - startTime
    };
  }
}

export async function executeBlurAction(action: BlurAction): Promise<ActionResult> {
  const startTime = Date.now();

  try {
    // Find the element
    let element = document.querySelector<HTMLElement>(action.selector);

    // Wait for element if specified
    if (!element && action.waitForElement) {
      element = await waitForElement<HTMLElement>(
        action.selector,
        action.waitForElement
      );
    }

    if (!element) {
      return {
        success: false,
        action,
        error: `Element not found: ${action.selector}`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: false
      };
    }

    // Check if visible
    if (!isElementVisible(element)) {
      return {
        success: false,
        action,
        error: 'Element is not visible',
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Blur the element
    element.blur();

    // Dispatch blur event
    const blurEvent = new FocusEvent('blur', {
      bubbles: true,
      cancelable: true,
      view: window
    });
    element.dispatchEvent(blurEvent);

    // Dispatch focusout event (bubbling version)
    const focusoutEvent = new FocusEvent('focusout', {
      bubbles: true,
      cancelable: true,
      view: window
    });
    element.dispatchEvent(focusoutEvent);

    return {
      success: true,
      action,
      timestamp: Date.now(),
      duration: Date.now() - startTime,
      elementFound: true
    };
  } catch (error) {
    return {
      success: false,
      action,
      error: error instanceof Error ? error.message : String(error),
      timestamp: Date.now(),
      duration: Date.now() - startTime
    };
  }
}
